import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { url: String }

  connect() {
    this.cellSelector = "[data-editable='true']"
    this.element.addEventListener("focusin", this.onFocusIn)
    this.element.addEventListener("focusout", this.onFocusOut)
    this.element.addEventListener("keydown", this.onKeyDown)
    this.element.addEventListener("paste", this.onPaste)
  }

  disconnect() {
    this.element.removeEventListener("focusin", this.onFocusIn)
    this.element.removeEventListener("focusout", this.onFocusOut)
    this.element.removeEventListener("keydown", this.onKeyDown)
    this.element.removeEventListener("paste", this.onPaste)
  }

  onFocusIn = (event) => {
    const cell = event.target.closest(this.cellSelector)
    if (!cell) return
    cell.dataset.original = cell.textContent.trim()
    cell.classList.add("team-grid__cell--editing")
  }

  onFocusOut = (event) => {
    const cell = event.target.closest(this.cellSelector)
    if (!cell) return
    cell.classList.remove("team-grid__cell--editing")
    const value = cell.textContent.trim()
    if (value !== (cell.dataset.original || "")) {
      this.save(cell, value)
    }
  }

  onKeyDown = (event) => {
    const cell = event.target.closest(this.cellSelector)
    if (!cell) return

    if (event.key === "Enter") {
      event.preventDefault()
      cell.blur()
      const next = this.cellBelow(cell)
      if (next) next.focus()
    } else if (event.key === "Escape") {
      event.preventDefault()
      cell.textContent = cell.dataset.original || ""
      cell.blur()
    } else if (event.key === "Tab") {
      // let browser handle, but commit on blur
    }
  }

  onPaste = (event) => {
    const cell = event.target.closest(this.cellSelector)
    if (!cell) return
    const text = (event.clipboardData || window.clipboardData).getData("text")
    if (text == null) return

    // Normalize line endings; trim a single trailing newline (Excel/Sheets habit).
    const lines = text.replace(/\r\n?/g, "\n").replace(/\n$/, "").split("\n")

    if (lines.length <= 1) return // let the browser paste a single value normally

    event.preventDefault()
    const field = cell.dataset.field
    const startRow = cell.closest("tr")
    let row = startRow
    for (const line of lines) {
      if (!row) break
      const target = row.querySelector(`[data-field='${field}']`)
      if (target && target.dataset.editable === "true") {
        const original = target.textContent.trim()
        const nextValue = line.trim()
        if (nextValue !== original) {
          target.textContent = nextValue
          this.save(target, nextValue)
        }
      }
      row = row.nextElementSibling
    }
  }

  cellBelow(cell) {
    const field = cell.dataset.field
    const row = cell.closest("tr")
    const nextRow = row && row.nextElementSibling
    return nextRow && nextRow.querySelector(`[data-field='${field}'][data-editable='true']`)
  }

  async save(cell, value) {
    const customerId = cell.dataset.customerId
    const field = cell.dataset.field
    cell.classList.remove("team-grid__cell--saved", "team-grid__cell--error")
    cell.classList.add("team-grid__cell--saving")

    try {
      const res = await fetch(this.urlValue, {
        method: "PATCH",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken(),
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({ customer_id: customerId, field: field, value: value })
      })
      const data = await res.json().catch(() => ({}))
      cell.classList.remove("team-grid__cell--saving")
      if (res.ok && data.ok) {
        cell.dataset.original = (data.value ?? "").toString()
        cell.textContent = cell.dataset.original
        cell.classList.add("team-grid__cell--saved")
        setTimeout(() => cell.classList.remove("team-grid__cell--saved"), 800)
      } else {
        cell.textContent = cell.dataset.original || ""
        cell.classList.add("team-grid__cell--error")
        if (data.error) console.warn("Inline edit failed:", data.error)
      }
    } catch (err) {
      cell.classList.remove("team-grid__cell--saving")
      cell.textContent = cell.dataset.original || ""
      cell.classList.add("team-grid__cell--error")
      console.error(err)
    }
  }

  csrfToken() {
    const el = document.querySelector("meta[name='csrf-token']")
    return el ? el.getAttribute("content") : ""
  }
}
